import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AtLimit } from '../components/ui/char-counter';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { FolderOpen, Eye, Plus, Edit2, Trash2, AlertCircle, ChevronLeft, ChevronRight, Layers } from 'lucide-react';
import { buildingTypeService } from '../services/buildingTypeService';
import { workCategoryService } from '../services/workCategoryService';
import { BuildingType, WorkCategory } from '../types/index';
import LoadingScreen from '../components/ui/LoadingScreen';
import { showSuccess, showWarning } from '../components/Alerts';
import {
  handleCreateError, handleUpdateError, handleDeleteError, handleLoadError,
} from '../utils/errorHandler';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../constants/permissions';
import RequirePermission from '../components/RequirePermission';

const MAX_VALUE = 100
const MAX_DESC = 200
const ITEMS_PER_PAGE = 8

// ── Extracted outside component to keep a stable reference and avoid focus loss on re-render ──
const handlePctInput = (onPctChange: (id: number, pct: number) => void, catId: number, rawValue: string) => {
  if (rawValue === '') {
    onPctChange(catId, 0);
    return;
  }
  if (!/^\d{0,3}$/.test(rawValue)) return;
  const num = parseInt(rawValue, 10);
  if (num > 100) return;
  onPctChange(catId, num);
};

const getPctDisplay = (pct: number | undefined) => {
  return pct === 0 || pct === undefined ? '' : String(pct);
};

const CategorySelector: React.FC<{
  categories: WorkCategory[];
  selected: { workCategoryId: number; percentage: number }[];
  total: number;
  onToggle: (id: number) => void;
  onPctChange: (id: number, pct: number) => void;
}> = ({ categories, selected, total, onToggle, onPctChange }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <Label className="text-xs font-medium text-foreground sm:text-sm">
        Rubros asociados{' '}
        <span className="text-muted-foreground font-normal">({selected.length} seleccionados)</span>
      </Label>
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold tabular-nums ${
        total === 100
          ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
          : total > 100
          ? 'bg-red-50 text-destructive dark:bg-red-900/20'
          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
      }`}>
        {total}%
        {total !== 100 && (
          <span className="text-xs font-normal opacity-75">
            (falta {100 - total}%)
          </span>
        )}
      </div>
    </div>
    <div className="border border-border rounded-lg overflow-hidden max-h-72 overflow-y-auto">
      {categories.length === 0 ? (
        <p className="py-6 text-sm text-center text-muted-foreground">No hay rubros disponibles</p>
      ) : (
        <div className="divide-y divide-border">
          {categories.map(cat => {
            const isSel = selected.some(c => c.workCategoryId === cat.id);
            const selCat = selected.find(c => c.workCategoryId === cat.id);
            return (
              <div
                key={cat.id}
                className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                  isSel ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-muted/40'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => onToggle(cat.id)}
                  className="w-4 h-4 rounded accent-primary cursor-pointer"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{cat.value}</p>
                  {cat.description && <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>}
                </div>
                {isSel && (
                  <div
                    className="flex items-center overflow-hidden rounded-lg border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-900 shadow-sm focus-within:ring-2 focus-within:ring-primary/50 focus-within:ring-offset-0"
                    onClick={e => e.stopPropagation()}
                  >
                    <input
                      type="text"
                      inputMode="numeric"
                      value={getPctDisplay(selCat?.percentage)}
                      onChange={e => handlePctInput(onPctChange, cat.id, e.target.value)}
                      onFocus={e => e.target.select()}
                      placeholder="0"
                      className="w-14 h-9 text-sm text-center tabular-nums bg-transparent outline-none focus:outline-none focus:ring-0 dark:text-neutral-200 dark:placeholder-neutral-500"
                    />
                    <div className="inline-flex shrink-0 items-center px-2.5 min-w-fit border-l border-gray-200 bg-gray-50 dark:border-neutral-600 dark:bg-neutral-700 h-9">
                      <span className="text-xs font-medium text-gray-500 dark:text-neutral-300">%</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  </div>
);

const ProjectItemConfig: React.FC = () => {
  const { hasPermission } = useAuth();
  const canEditConfig = hasPermission(PERMISSIONS.CONFIGURACION.EDITAR);

  const [buildingTypes, setBuildingTypes] = useState<BuildingType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [workCategories, setWorkCategories] = useState<WorkCategory[]>([]);

  // Paginación
  const [page, setPage] = useState(1);

  // Detail
  const [selectedBuildingType, setSelectedBuildingType] = useState<BuildingType | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Create
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    value: '',
    description: '',
    selectedCategories: [] as { workCategoryId: number; percentage: number }[]
  });

  // Delete
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [buildingTypeToDelete, setBuildingTypeToDelete] = useState<BuildingType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Update
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [buildingTypeToUpdate, setBuildingTypeToUpdate] = useState<BuildingType | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateFormData, setUpdateFormData] = useState({
    value: '',
    description: '',
    selectedCategories: [] as { workCategoryId: number; percentage: number }[]
  });

  useEffect(() => {
    loadBuildingTypes();
    loadWorkCategories();
  }, []);

  const loadBuildingTypes = async () => {
    setIsLoading(true);
    try {
      const response = await buildingTypeService.getAllBuildingTypesWithWorkTypes();
      if (response?.success && response?.data) setBuildingTypes(response.data);
    } catch (e) {
      handleLoadError(e, 'los tipos de obra');
      setBuildingTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const loadWorkCategories = async () => {
    try {
      const response = await workCategoryService.getAllWorkCategories();
      setWorkCategories(response);
    } catch (e) {
      handleLoadError(e, 'los rubros');
    }
  };

  // Paginación calculada
  const totalPages = Math.ceil(buildingTypes.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageItems = buildingTypes.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  // ── Create helpers ──
  const resetForm = () => setFormData({ value: '', description: '', selectedCategories: [] });

  const createTotal = () => formData.selectedCategories.reduce((t, c) => t + (c.percentage || 0), 0);

  const toggleCategory = (id: number) => {
    setFormData(prev => {
      const has = prev.selectedCategories.some(c => c.workCategoryId === id);
      return {
        ...prev,
        selectedCategories: has
          ? prev.selectedCategories.filter(c => c.workCategoryId !== id)
          : [...prev.selectedCategories, { workCategoryId: id, percentage: 0 }]
      };
    });
  };

  const updateCategoryPct = (id: number, pct: number) => {
    if (pct < 0 || pct > 100) return;
    setFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.map(c => c.workCategoryId === id ? { ...c, percentage: pct } : c)
    }));
  };

  const handleCreate = async () => {
    if (!canEditConfig) return;
    if (!formData.value.trim()) {
      showWarning('Por favor, completa el campo obligatorio', 'Nombre del tipo de obra');
      document.getElementById('create-bt-value')?.focus();
      return;
    }
    if (formData.selectedCategories.length === 0) {
      showWarning('Seleccioná al menos un rubro', 'Rubros asociados');
      return;
    }
    if (createTotal() !== 100) {
      showWarning('Porcentajes inválidos', `Los porcentajes deben sumar exactamente 100%. Actualmente: ${createTotal()}%`);
      return;
    }
    setIsCreating(true);
    try {
      await buildingTypeService.createBuildingTypeWithWorkTypes({
        value: formData.value.trim(),
        description: formData.description.trim(),
        workCategories: formData.selectedCategories
      });
      await loadBuildingTypes();
      setShowCreateModal(false);
      resetForm();
      showSuccess('Tipo de obra creado exitosamente');
    } catch (e) {
      handleCreateError(e, 'el tipo de obra');
    } finally {
      setIsCreating(false);
    }
  };

  // ── Delete helpers ──
  const handleDelete = async () => {
    if (!buildingTypeToDelete) return;
    setIsDeleting(true);
    try {
      await buildingTypeService.deleteBuildingType(buildingTypeToDelete.id);
      await loadBuildingTypes();
      setShowDeleteModal(false);
      setBuildingTypeToDelete(null);
      showSuccess('Tipo de obra eliminado exitosamente');
    } catch (e) {
      handleDeleteError(e, 'el tipo de obra');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Update helpers ──
  const openUpdate = (bt: BuildingType) => {
    if (!canEditConfig) return;
    setBuildingTypeToUpdate(bt);
    setUpdateFormData({
      value: bt.value,
      description: bt.description || '',
      selectedCategories: bt.workCategories.map(c => ({ workCategoryId: c.id, percentage: c.percentage }))
    });
    setShowUpdateModal(true);
  };

  const updateTotal = () => updateFormData.selectedCategories.reduce((t, c) => t + (c.percentage || 0), 0);

  const toggleUpdateCategory = (id: number) => {
    setUpdateFormData(prev => {
      const has = prev.selectedCategories.some(c => c.workCategoryId === id);
      return {
        ...prev,
        selectedCategories: has
          ? prev.selectedCategories.filter(c => c.workCategoryId !== id)
          : [...prev.selectedCategories, { workCategoryId: id, percentage: 0 }]
      };
    });
  };

  const updateUpdateCategoryPct = (id: number, pct: number) => {
    if (pct < 0 || pct > 100) return;
    setUpdateFormData(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.map(c => c.workCategoryId === id ? { ...c, percentage: pct } : c)
    }));
  };

  const handleSubmitUpdate = async () => {
    if (!canEditConfig || !buildingTypeToUpdate) return;
    if (!updateFormData.value.trim()) {
      showWarning('Por favor, completa el campo obligatorio', 'Nombre del tipo de obra');
      document.getElementById('update-bt-value')?.focus();
      return;
    }
    if (updateFormData.selectedCategories.length === 0) {
      showWarning('Seleccioná al menos un rubro', 'Rubros asociados');
      return;
    }
    if (updateTotal() !== 100) {
      showWarning('Porcentajes inválidos', `Los porcentajes deben sumar exactamente 100%. Actualmente: ${updateTotal()}%`);
      return;
    }
    setIsUpdating(true);
    try {
      await buildingTypeService.updateBuildingType(buildingTypeToUpdate.id, {
        value: updateFormData.value.trim(),
        description: updateFormData.description.trim(),
        workCategories: updateFormData.selectedCategories
      });
      await loadBuildingTypes();
      setShowUpdateModal(false);
      setBuildingTypeToUpdate(null);
      showSuccess('Tipo de obra actualizado exitosamente');
    } catch (e) {
      handleUpdateError(e, 'el tipo de obra');
    } finally {
      setIsUpdating(false);
    }
  };



  if (isLoading && buildingTypes.length === 0) {
    return <LoadingScreen message="Cargando tipos de obra" />;
  }

  return (
    <>
      {/* Toolbar */}
      <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{buildingTypes.length}</span>{' '}
          tipo{buildingTypes.length !== 1 ? 's' : ''} de obra configurado{buildingTypes.length !== 1 ? 's' : ''}
        </p>
        <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
          <Button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="flex items-center gap-2 rounded-lg h-9 px-4 text-sm"
          >
            <Plus className="w-4 h-4" />
            Nuevo Tipo de Obra
          </Button>
        </RequirePermission>
      </div>

      {/* Lista */}
      <div className="px-6 py-5">
        {buildingTypes.length === 0 ? (
          <div className="py-14 text-center">
            <FolderOpen className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
            <p className="text-sm font-medium text-foreground mb-1">No hay tipos de obra configurados</p>
            <p className="text-xs text-muted-foreground">Los tipos de obra aparecerán aquí una vez creados</p>
          </div>
        ) : (
          <>
            <div className="space-y-2.5">
              {pageItems.map(bt => (
                <div
                  key={bt.id}
                  className="p-4 border border-border rounded-lg bg-card hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-foreground">{bt.value}</h3>
                        <Badge
                          variant={bt.workCategories.length > 0 ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {bt.workCategories.length > 0
                            ? `${bt.workCategories.length} categoría${bt.workCategories.length !== 1 ? 's' : ''}`
                            : 'Sin categorías'}
                        </Badge>
                      </div>
                      {bt.description && (
                        <p className="text-xs text-muted-foreground mb-2">{bt.description}</p>
                      )}
                      {bt.workCategories.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                          {bt.workCategories.map(cat => (
                            <Badge key={cat.id} variant="outline" className="text-xs">
                              {cat.value} ({cat.percentage}%)
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedBuildingType(bt); setShowDetailModal(true); }}
                        className="rounded-lg h-8 w-8 p-0"
                        title="Ver detalle"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                      <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openUpdate(bt)}
                          className="rounded-lg h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300"
                          title="Editar"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </RequirePermission>
                      <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setBuildingTypeToDelete(bt); setShowDeleteModal(true); }}
                          className="rounded-lg h-8 w-8 p-0 hover:bg-destructive/10 hover:border-destructive"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-destructive" />
                        </Button>
                      </RequirePermission>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginación */}
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Mostrando{' '}
                <span className="font-semibold text-foreground">{pageItems.length}</span> de{' '}
                <span className="font-semibold text-foreground">{buildingTypes.length}</span>{' '}
                tipo{buildingTypes.length !== 1 ? 's' : ''}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1} className="rounded-lg">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-foreground tabular-nums">{safePage} / {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages} className="rounded-lg">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Dialog: Detail ── */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
              <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Eye className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              Detalles del Tipo de Obra
            </DialogTitle>
            {selectedBuildingType && (
              <DialogDescription>{selectedBuildingType.value}</DialogDescription>
            )}
          </DialogHeader>
          {selectedBuildingType && (
            <div className="space-y-4 py-2">
              {selectedBuildingType.description && (
                <p className="text-sm text-muted-foreground">{selectedBuildingType.description}</p>
              )}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground">Rubros asociados</h4>
                {selectedBuildingType.workCategories.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">No hay rubros asociados</p>
                ) : (
                  <div className="space-y-2">
                    {selectedBuildingType.workCategories.map(cat => (
                      <div key={cat.id} className="p-3 border border-border rounded-lg bg-muted/20 flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-foreground">{cat.value}</span>
                          {cat.description && (
                            <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                          )}
                        </div>
                        <Badge variant="outline">{cat.percentage}%</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="flex justify-end mt-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)} className="rounded-lg">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Create ── */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { if (!open) { setShowCreateModal(false); resetForm(); } }}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
              <div className="w-8 h-8 flex items-center justify-center bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <Plus className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              Crear Nuevo Tipo de Obra
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Completá el nombre, la descripción y asigná los rubros con sus porcentajes (deben sumar 100%).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="create-bt-value" className="text-xs font-medium text-foreground sm:text-sm">
                  Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
                </Label>
                <Input
                  id="create-bt-value"
                  placeholder="Ej: Casa unifamiliar..."
                  value={formData.value}
                  onChange={e => { if (e.target.value.length <= MAX_VALUE) setFormData(prev => ({ ...prev, value: e.target.value })) }}
                  maxLength={MAX_VALUE}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={formData.value} max={MAX_VALUE} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-bt-desc" className="text-xs font-medium text-foreground sm:text-sm">Descripción</Label>
                <Input
                  id="create-bt-desc"
                  placeholder="Descripción del tipo..."
                  value={formData.description}
                  onChange={e => { if (e.target.value.length <= MAX_DESC) setFormData(prev => ({ ...prev, description: e.target.value })) }}
                  maxLength={MAX_DESC}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={formData.description} max={MAX_DESC} />
              </div>
            </div>

            <div className="border-t border-border"></div>

            <CategorySelector
              categories={workCategories}
              selected={formData.selectedCategories}
              total={createTotal()}
              onToggle={toggleCategory}
              onPctChange={updateCategoryPct}
            />

            {formData.selectedCategories.length > 0 && createTotal() !== 100 && (
              <div className="px-4 py-3 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/50 dark:bg-amber-900/20">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Los porcentajes deben sumar exactamente 100%. Actualmente: {createTotal()}%
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }} className="border-input hover:bg-accent text-foreground rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button onClick={handleCreate} disabled={isCreating} className="rounded-lg text-white bg-indigo-600 hover:bg-indigo-700">
                {isCreating ? 'Creando...' : 'Crear Tipo de Obra'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Delete confirm ── */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-foreground">
              <AlertCircle className="w-5 h-5 text-destructive" />
              Eliminar Tipo de Obra
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              ¿Estás seguro de que deseas eliminar{' '}
              <span className="font-medium text-foreground">"{buildingTypeToDelete?.value}"</span>?
              Esta acción es irreversible y se eliminarán todos los datos asociados.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} className="rounded-lg">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button variant="destructive" onClick={handleDelete} disabled={isDeleting} className="rounded-lg">
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Dialog: Update ── */}
      <Dialog open={showUpdateModal} onOpenChange={(open) => { if (!open) setShowUpdateModal(false); }}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-base font-semibold text-foreground">
              <div className="w-8 h-8 flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Edit2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
              Actualizar Tipo de Obra
            </DialogTitle>
            {buildingTypeToUpdate && (
              <DialogDescription className="text-muted-foreground">
                Editando: <span className="font-medium text-foreground">{buildingTypeToUpdate.value}</span>
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="update-bt-value" className="text-xs font-medium text-foreground sm:text-sm">
                  Nombre <span className="text-xs text-red-500">(Obligatorio)</span>
                </Label>
                <Input
                  id="update-bt-value"
                  placeholder="Nombre del tipo de obra..."
                  value={updateFormData.value}
                  onChange={e => { if (e.target.value.length <= MAX_VALUE) setUpdateFormData(prev => ({ ...prev, value: e.target.value })) }}
                  maxLength={MAX_VALUE}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={updateFormData.value} max={MAX_VALUE} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="update-bt-desc" className="text-xs font-medium text-foreground sm:text-sm">Descripción</Label>
                <Input
                  id="update-bt-desc"
                  placeholder="Descripción..."
                  value={updateFormData.description}
                  onChange={e => { if (e.target.value.length <= MAX_DESC) setUpdateFormData(prev => ({ ...prev, description: e.target.value })) }}
                  maxLength={MAX_DESC}
                  className="rounded-lg bg-background border-border focus-visible:ring-primary"
                />
                <AtLimit value={updateFormData.description} max={MAX_DESC} />
              </div>
            </div>

            <div className="border-t border-border"></div>

            <CategorySelector
              categories={workCategories}
              selected={updateFormData.selectedCategories}
              total={updateTotal()}
              onToggle={toggleUpdateCategory}
              onPctChange={updateUpdateCategoryPct}
            />

            {updateFormData.selectedCategories.length > 0 && updateTotal() !== 100 && (
              <div className="px-4 py-3 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/50 dark:bg-amber-900/20">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Los porcentajes deben sumar exactamente 100%. Actualmente: {updateTotal()}%
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowUpdateModal(false)} className="border-input hover:bg-accent text-foreground rounded-lg">Cancelar</Button>
            <RequirePermission permission={PERMISSIONS.CONFIGURACION.EDITAR}>
              <Button onClick={handleSubmitUpdate} disabled={isUpdating} className="rounded-lg text-white bg-indigo-600 hover:bg-indigo-700">
                {isUpdating ? 'Actualizando...' : 'Actualizar Tipo de Obra'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectItemConfig;
